'use client';

import { useIsMobile } from '@/hooks/use-mobile';

import { useCallback, useMemo, useState } from 'react';

import FormWidget from '@/components/form-widget';
import { TemplateColor } from '@/components/template-page-layout/template-page-layout.types';
import { Button } from '@/components/ui/button';
import DatePickerSecondary from '@/components/ui/date-picker-secondary';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';

import Modal from '@/shared/components/Modal';
import { COPY } from '@/shared/constants/copies';
import { TFormWidget } from '@/shared/services/forms/forms.types';

import {
	FORM_WIDGET_APPLICATIONS_THRESHOLD,
	FORM_WIDGET_RESPONSE_RATE_THRESHOLD,
} from '@/utils/constants';
import { cn } from '@/utils/styles';

import { LocationPickerPopover } from './location-widget-popover';

type WidgetConfig = {
	widgetType: TFormWidget;
	text: string;
	description: string;
};

interface ILiveStatsDialogProps {
	formId: string;
	formWidgets: Array<TFormWidget>;
	formClosingDate: string | null | undefined;
	formClosingDateText: string;
	formTotalCountText: string;
	formResponsePercentageText: string;
	formLocationText: string;
	locations: string[];
	color?: TemplateColor;
	handleIncludedWidgetsUpdate: (
		includedWidgets: TFormWidget[],
		updatedClosingDate: string | null
	) => void;
	handleClosingDateUpdate: (closingDate: string | null) => void;
	handleLocationsUpdate: (locations: string[]) => void;
	onWidgetsModalOpen?: () => void;
	onWidgetsModalClose?: () => void;
}

// New props for modal control
interface LiveStatsDialogModalProps extends ILiveStatsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

function LiveStatsDialogModal({
	formWidgets,
	formClosingDate,
	handleIncludedWidgetsUpdate,
	handleClosingDateUpdate,
	handleLocationsUpdate,
	formClosingDateText,
	formTotalCountText,
	formResponsePercentageText,
	locations,
	onWidgetsModalOpen,
	onWidgetsModalClose,
	color = 'blue',
	open,
	onOpenChange,
}: LiveStatsDialogModalProps) {
	const [datePopoverOpen, setDatePopoverOpen] = useState<boolean>(false);
	const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);
	const isMobile = useIsMobile();

	const formClosingDateSet = useMemo(() => {
		if (!formClosingDate) {
			const updatedDate = new Date();
			updatedDate.setDate(updatedDate.getDate() + 7);
			updatedDate.setHours(23, 59, 59, 999);
			return updatedDate;
		}
		return new Date(formClosingDate);
	}, [formClosingDate]);

	const updateClosingDate = (date: Date | undefined) => {
		if (formWidgets.includes('CLOSING_DATE')) {
			if (date) {
				date.setHours(23, 59, 59, 999);
				handleClosingDateUpdate(date.toISOString());
			} else {
				handleClosingDateUpdate('');
			}
			setDatePopoverOpen(false);
		}
	};

	const updateFormWidgetsToShow = useCallback(
		(widget: TFormWidget) => (checked: boolean) => {
			const widgetsToUpdate = checked
				? [...formWidgets, widget]
				: formWidgets.filter((w) => w !== widget);

			const updatedClosingDate = widgetsToUpdate.includes('CLOSING_DATE')
				? formClosingDateSet.toISOString()
				: '';

			handleIncludedWidgetsUpdate(widgetsToUpdate, updatedClosingDate);

			if (widget === 'LOCATION' && !checked) {
				handleLocationsUpdate([]);
			}
		},
		[formWidgets, formClosingDateSet, handleIncludedWidgetsUpdate, handleLocationsUpdate]
	);

	const handleDialogOpenChange = useCallback(
		(open: boolean) => {
			onOpenChange(open);
			if (open) {
				onWidgetsModalOpen?.();
			} else {
				onWidgetsModalClose?.();
			}
		},
		[onOpenChange, onWidgetsModalOpen, onWidgetsModalClose]
	);

	const handleLocationsUpdateWithAutoEnable = useCallback(
		(newLocations: string[]) => {
			handleLocationsUpdate(newLocations);
			if (newLocations.length > 0 && !formWidgets.includes('LOCATION')) {
				const widgetsToUpdate = [...formWidgets, 'LOCATION' as TFormWidget];

				const updatedClosingDate = widgetsToUpdate.includes('CLOSING_DATE')
					? formClosingDateSet.toISOString()
					: '';
				handleIncludedWidgetsUpdate(widgetsToUpdate, updatedClosingDate);
			}
		},
		[handleLocationsUpdate, formWidgets, handleIncludedWidgetsUpdate, formClosingDateSet]
	);

	/** Location display text for the widget preview */
	const locationPreviewText = useMemo(() => {
		if (locations.length === 0) return 'Add Location';
		if (locations.length === 1) return locations[0];
		const extra = locations.length - 2;
		const base = locations.slice(0, 2).join(', ');
		return extra > 0 ? `${base} +${extra}` : base;
	}, [locations]);

	const widgetConfigs = useMemo<WidgetConfig[]>(
		() => [
			{
				widgetType: 'LOCATION',
				text: locationPreviewText,
				description: COPY.formSettings.widgetLocationHelp,
			},
			{
				widgetType: 'CLOSING_DATE',
				text: formClosingDateText,
				description: COPY.formSettings.widgetDeadlineHelp,
			},
			{
				widgetType: 'NUMBER_OF_APPLICANTS',
				text: formTotalCountText,
				description: COPY.formSettings.widgetApplicantsHelp(FORM_WIDGET_APPLICATIONS_THRESHOLD),
			},
			{
				widgetType: 'RESPONSE_PERCENTAGE',
				text: formResponsePercentageText,
				description: COPY.formSettings.widgetResponseRateHelp(FORM_WIDGET_RESPONSE_RATE_THRESHOLD),
			},
		],
		[locationPreviewText, formClosingDateText, formTotalCountText, formResponsePercentageText]
	);

	return (
		<Modal
			open={open}
			onOpenChange={handleDialogOpenChange}
			titleContent="Widgets"
			contentClassName="max-w-screen-sm"
		>
			<div className="flex flex-col gap-8 py-4">
				{widgetConfigs.map(({ widgetType, text, description }) => (
					<div
						key={widgetType}
						className="flex items-center justify-between gap-2"
					>
						<div className="flex flex-1 flex-col gap-2">
							<div className="flex items-center gap-2">
								{widgetType === 'LOCATION' ? (
									<LocationPickerPopover
										open={locationPopoverOpen}
										onOpenChange={setLocationPopoverOpen}
										selectedLocations={locations}
										onLocationsChange={handleLocationsUpdateWithAutoEnable}
										text={text}
										color={color}
									/>
								) : (
									<div className="min-w-[100px] max-w-[270px]">
										<FormWidget
											widgetType={widgetType}
											text={text}
											color={color}
											variant="minimal"
										/>
									</div>
								)}
								{widgetType === 'CLOSING_DATE' && (
									<Popover
										open={datePopoverOpen}
										onOpenChange={setDatePopoverOpen}
										modal={true}
									>
										<PopoverTrigger asChild>
											<Button
												variant="link"
												className={cn(
													'px-0 text-blue-600',
													!formWidgets.includes('CLOSING_DATE') && 'invisible'
												)}
											>
												Change
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-auto p-0">
											<DatePickerSecondary
												selectedDate={formClosingDateSet ? new Date(formClosingDateSet) : undefined}
												onSelectDate={updateClosingDate}
											/>
										</PopoverContent>
									</Popover>
								)}
							</div>
							<p className="pl-4 text-sm text-secondary">{description}</p>
						</div>
						<div className="shrink-0 self-start pt-2 sm:pt-3">
							<Switch
								size={isMobile ? 'sm' : undefined}
								checked={formWidgets.includes(widgetType)}
								onCheckedChange={updateFormWidgetsToShow(widgetType)}
								className="data-[state=checked]:bg-primary"
							/>
						</div>
					</div>
				))}
			</div>
		</Modal>
	);
}

export default LiveStatsDialogModal;
